import * as React from "react";

/**
 * The bottom of the conversation — where the reader wants to be, and the only
 * place from which "he has seen the newest message" is a true statement.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE SENTINEL ANSWERS BOTH QUESTIONS, AND THAT IS WHY THEY LIVE IN ONE HOOK.
 *
 * A zero-height element is rendered AFTER the last bubble. Two things follow
 * from watching it:
 *
 *   1. OPEN AT THE END. The list is rendered oldest-first inside a 60vh box, so
 *      the natural scroll position — the top — is the OLDEST message of the
 *      NEWEST page. The server pages backwards precisely so the newest fifty
 *      arrive first (`use-messages.ts`); landing on their oldest end throws that
 *      away and makes the owner scroll down every single time he opens the
 *      screen. So the sentinel is scrolled into view when the conversation first
 *      paints, and again whenever a NEW message arrives WHILE HE IS ALREADY AT
 *      THE BOTTOM.
 *
 *   2. AND ONLY THEN IS THE WATERMARK ALLOWED TO MOVE. `newestOnScreen` is what
 *      `POST /messages/read` is gated on, because the id it carries is defined
 *      as "the newest message the reader has actually had on screen"
 *      (`packages/types/src/messages.ts`), and the table stores exactly that
 *      much and no more — "it knows only which were on screen"
 *      (`prisma/sql/14_messages.sql`).
 *
 * WHAT IT DELIBERATELY DOES NOT DO IS FOLLOW THE READER DOWN THE PAGE. Loading
 * older messages does not move the view: the scroll is keyed on the NEWEST id,
 * which "Show older messages" does not change. A screen that yanked itself back
 * to the bottom after fetching last March would make the button useless.
 *
 * NO INTERSECTION OBSERVER, NO WATERMARK. `IntersectionObserver` is in every
 * browser this app runs in, but if it is ever missing the hook reports FALSE
 * rather than true: an environment that cannot tell what is on screen must not
 * claim the reader saw it. The cost of being wrong that way is a badge shown
 * twice; the cost of the other way is a message nobody ever sees again, since
 * there is no per-message read state and no way to mark anything unread.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface ConversationEnd {
  /** Attach to a zero-height element rendered AFTER the last message. */
  endRef: React.RefObject<HTMLDivElement | null>;
  /** True only while that element is genuinely inside the scrolled viewport. */
  newestOnScreen: boolean;
}

export function useConversationEnd(newestId: number | null): ConversationEnd {
  const endRef = React.useRef<HTMLDivElement | null>(null);
  const [newestOnScreen, setNewestOnScreen] = React.useState(false);
  /*
   * The same fact as the state above, readable from an effect that must not
   * re-run when it changes. The scroll below asks "was he at the bottom BEFORE
   * this message arrived?", and putting `newestOnScreen` in that effect's
   * dependencies would re-run it when the answer changes rather than when a
   * message arrives.
   */
  const onScreenRef = React.useRef(false);

  React.useEffect(() => {
    const end = endRef.current;
    if (end === null) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver((entries) => {
      /*
       * THE LAST ENTRY, NOT `.some(...)`. The callback is handed a queue of
       * changes for the elements it watches, so with several batched together
       * the current state is the newest one — `some` would report "on screen"
       * from a stale entry the reader has already scrolled past.
       */
      const latest = entries[entries.length - 1];
      const visible = latest?.isIntersecting ?? false;
      onScreenRef.current = visible;
      setNewestOnScreen(visible);
    });

    observer.observe(end);
    return () => {
      observer.disconnect();
    };
  }, []);

  /*
   * `useLayoutEffect`, not `useEffect`: this runs before the browser paints, so
   * the conversation appears at its end rather than appearing at its beginning
   * and jumping.
   */
  const scrolledToRef = React.useRef<number | null>(null);
  React.useLayoutEffect(() => {
    if (newestId === null) return;
    if (scrolledToRef.current === newestId) return;

    const firstPaint = scrolledToRef.current === null;
    scrolledToRef.current = newestId;

    // The first paint always lands at the end. After that, only follow a new
    // message when the reader was already there — otherwise he is reading
    // something further up and being dragged away from it is the bug.
    if (!firstPaint && !onScreenRef.current) return;

    /*
     * `block: "nearest"` scrolls the least that puts the end in view, and does
     * nothing at all when it already is. `"end"` would re-scroll the page on
     * every arrival even when the bottom was visible the whole time.
     */
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [newestId]);

  return { endRef, newestOnScreen };
}
